import React, { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useRouter } from "next/router";
import getTranslatedString from "@helpers/GetTranslatedString";
import BorderedContainer from "@components/ui/bordered_container";
import styles from "./NavigatableListView.module.scss";

export default function NavigatableListView({
    useDataHook,
    titleValue,
    onBack,
    menuItem,
    onItemClick,
}) {
    const router = useRouter();
    const { data, error } = useDataHook ? useDataHook() : { data: null, error: null };
    const containerRef = useRef(null);

    // Swipe gesture handling
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let touchStartX = 0;
        let touchEndX = 0;

        const handleTouchStart = (e) => {
            touchStartX = e.changedTouches[0].screenX;
        };

        const handleTouchEnd = (e) => {
            touchEndX = e.changedTouches[0].screenX;
            handleSwipe();
        };

        const handleSwipe = () => {
            const swipeDistance = touchEndX - touchStartX;
            // Swipe til hægri (>100px) fer til baka
            if (swipeDistance > 100) {
                onBack();
            }
        };

        container.addEventListener("touchstart", handleTouchStart);
        container.addEventListener("touchend", handleTouchEnd);

        return () => {
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchend", handleTouchEnd);
        };
    }, [onBack]);

    const handleItemClick = (item) => {
        // Navigate to detail page using menuItem.link as base
        router.push(`${menuItem.link}/${item.id}`);

        // Call optional callback (e.g., close mobile menu)
        if (onItemClick) {
            onItemClick(item);
        }
    };

    // Check if an item is currently selected
    const isItemSelected = (item) => {
        // Normalize paths by removing trailing slashes and query params
        const currentPath = router.asPath.split("?")[0].replace(/\/$/, "");
        const itemPath = `${menuItem.link}/${item.id}`.replace(/\/$/, "");
        return currentPath === itemPath;
    };

    return (
        <>
            <BorderedContainer>
                <div className={styles.header}>
                    <button onClick={onBack} className={styles.backButton}>
                        <FontAwesomeIcon icon={faChevronLeft} style={{ height: 12, width: 12 }} />
                        <span>Til baka</span>
                    </button>
                    <a
                        href={menuItem.link}
                        onClick={(e) => {
                            e.preventDefault();
                            onBack();
                            router.push(menuItem.link);
                        }}
                        className={styles.title}
                    >
                        {getTranslatedString({
                            is: menuItem.title,
                            en: menuItem.title_en,
                        })}
                    </a>
                </div>
            </BorderedContainer>
            <BorderedContainer className={styles.container} ref={containerRef}>
                <div className={styles.listContainer}>
                    {!data && !error && <div className={styles.loading}>Hleð...</div>}
                    {error && <div className={styles.error}>Villa við að sækja gögn</div>}
                    {data &&
                        data.map((item) => (
                            <div
                                key={item.id}
                                className={`${styles.listItem} ${isItemSelected(item) ? styles.selected : ""}`}
                                onClick={() => handleItemClick(item)}
                            >
                                <span>{item[titleValue]}</span>
                                <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
                            </div>
                        ))}
                </div>
            </BorderedContainer>
        </>
    );
}
